import { useMemo, useState } from "react";
import {
  Field,
  formValueSelector,
  reduxForm,
  SubmissionError,
} from "redux-form";
import { Button, Input, Checkbox } from "expano-components";
import { connect } from "react-redux";

import { Main, Dashboard, Select } from "components";
import product_status_options from "utils/select_options/product_status_options";
import { getKindLabel } from "utils/select_options/product_kind_options";

import useGet from "hooks/useGet";
import useCreate from "hooks/useCreate";

import { ReactComponent as ProductIcon } from "icons/product.svg";

const InputField = (props) => <Input {...props} />;
const SelectField = (props) => <Select {...props} />;
const CheckField = (props) => <Checkbox {...props} />;

const ProductNew = ({
  product_status,
  is_bundle,
  is_virtual,
  handleSubmit,
  change,
  pristine,
  history,
  invalid,
}) => {
  const [product_group_id, setProductGroupId] = useState(null);

  const { data: product_groups } = useGet({
    query: ["product_groups"],
    path: "/product_groups",
    context: "list",
    search: { per_page: 10_000 },
  });

  const { data: product_group } = useGet({
    query: ["product_groups", product_group_id],
    path: `/product_groups/${product_group_id}`,
    context: "ui_product_show",
    options: {
      enabled: !!product_group_id,
    },
  });

  const { is_creating, create } = useCreate({
    path: "/products",
    onSuccess: ({ id }) => {
      history.push(`/products/${id}`);
    },
    onError: (err) => {
      throw new SubmissionError(
        Object.fromEntries(
          Object.entries(err).map(([key, value]) => {
            if (["producer", "brand", "root_taxon"].includes(key)) {
              return [`${key}_id`, value];
            }
            return [key, value];
          }),
        ),
      );
    },
  });

  const product_group_options = useMemo(() => {
    return product_groups?.map(({ id, name, sku, kind }) => ({
      label: `[${sku}] ${name} (${getKindLabel(kind)})`,
      value: id,
    }));
  }, [product_groups]);

  const onSubmit = (values) => create(values);

  return (
    <Main
      page={{
        name: "Dodaj produkt",
        icon: <ProductIcon />,
        breadcrumbs: [
          {
            name: "Produkty",
            path: "/products",
          },
          { name: "Dodaj produkt" },
        ],
      }}
    >
      <div className="dashboard-wrapper-flex">
        <div className="column-half">
          <Dashboard
            header={{ title: "Podstawowe informacje", icon: "info" }}
            footer={
              <>
                <Button
                  type="cancel"
                  onClick={() => history.push("/products")}
                />
                <Button
                  type="save"
                  text={is_creating ? "Proszę czekać..." : "Utwórz"}
                  disabled={pristine || invalid || is_creating}
                  onClick={handleSubmit(onSubmit)}
                />
              </>
            }
          >
            <form
              className="dashboard__form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <Field
                label="Produkt grupowy"
                name="product_group_id"
                component={SelectField}
                options={product_group_options}
                handleChange={(value) => {
                  change("product_group_id", value);
                  setProductGroupId(value);
                }}
              />
              <Field
                name="name"
                label="Nazwa produktu"
                component={InputField}
                type="text"
              />
              <div className="row m-b-10">
                <div className="box-6">
                  <Field
                    name="sku"
                    label="SKU"
                    component={InputField}
                    type="text"
                  />
                </div>
                <div className="box-6">
                  <Input
                    label="Kategoria"
                    input={{
                      name: "root_taxon_id",
                      disabled: true,
                      value: product_group?.root_taxon
                        ? `${product_group?.root_taxon?.name} [${product_group?.root_taxon?.identifier}]`
                        : null,
                    }}
                    meta={{}}
                  />
                </div>
              </div>
              <div className="row m-b-10">
                <div className="box-6">
                  <Input
                    label="Rodzaj (dziedziczony z produktu grupowego)"
                    input={{
                      name: "kind",
                      disabled: true,
                      value: product_group?.kind
                        ? getKindLabel(product_group.kind)
                        : "",
                    }}
                    meta={{}}
                  />
                </div>
              </div>
              <div className="row m-b-10">
                <div className="box-6">
                  <Input
                    label="Producent"
                    input={{
                      name: "producer_id",
                      disabled: true,
                      value: product_group?.producer?.name,
                    }}
                    meta={{}}
                  />
                </div>
                <div className="box-6">
                  <Input
                    label="Marka"
                    input={{
                      name: "brand_id",
                      disabled: true,
                      value: product_group?.brand?.name,
                    }}
                    meta={{}}
                  />
                </div>
              </div>
              <div className="row m-b-10">
                <div className="box-6">
                  <Field
                    name="producer_code"
                    label="Kod producenta"
                    component={InputField}
                    type="text"
                  />
                </div>
                <div className="box-6">
                  <Field
                    name="ean"
                    label="EAN"
                    component={InputField}
                    type="text"
                  />
                </div>
              </div>
              <div className="row m-b-10">
                <div className="box-6">
                  <Field
                    label="Status produktu"
                    name="status"
                    component={SelectField}
                    options={product_status_options}
                    defaultValue={product_status_options.find(
                      ({ value }) => value === product_status,
                    )}
                    handleChange={(val) => change("status", val)}
                  />
                </div>
                <div className="box-6">
                  <Field
                    label="Produkt jest wirtualny"
                    name="is_virtual"
                    info="Posida substytucyjne produkty"
                    component={CheckField}
                    disabled={is_bundle}
                  />
                </div>
              </div>
              <div className="row">
                <div className="box-6">
                  <Field
                    label="Produkt jest bundlem"
                    info="Powstaje z kompletacji innych produktów"
                    name="is_bundle"
                    component={CheckField}
                    disabled={is_virtual}
                  />
                </div>
                <div className="box-6">
                  {product_status === "active" ? (
                    <Field
                      name="has_infinite_sales_stock"
                      label="Posiada nielimitowane stany"
                      info="Jeśli tak, stany magazynowe nię będą synchronizowane z realnym stanem."
                      component={CheckField}
                      disabled={is_bundle || is_virtual}
                    />
                  ) : (
                    <div className="form-group">
                      <div className="check-group disabled">
                        <input
                          disabled
                          name="has_infinite_sales_stock_readonly"
                          type="checkbox"
                        />
                        <label
                          htmlFor="has_infinite_sales_stock_readonly"
                          className="label-form"
                        >
                          Posiada nielimitowane stany
                        </label>
                        <p className="additional-info">
                          Jeśli tak, stany magazynowe nię będą synchronizowane z
                          realnym stanem.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="box-6">
                  <Field
                    label="Mnożnik ceny sugerowanej"
                    name="suggested_price_multiplier"
                    component={InputField}
                  />
                </div>
              </div>
            </form>
          </Dashboard>
        </div>
      </div>
    </Main>
  );
};

const validate = (values) => {
  const errors = {};
  if (!values.product_group_id) {
    errors.product_group_id = "Produkt grupowy jest wymagany!";
  }
  if (!values.name) {
    errors.name = "Nazwa produktu jest wymagana!";
  }

  if (!values.sku) {
    errors.sku = "SKU produktu jest wymagane!";
  }

  return errors;
};

const form = "create-product";

const selector = formValueSelector(form);

export default connect((state) => ({
  initialValues: {
    name: "",
    sku: "",
    producer_code: "",
    ean: "",
    is_bundle: false,
    is_virtual: false,
    has_infinite_sales_stock: false,
    status: "active",
    suggested_price_multiplier: 1,
  },
  product_status: selector(state, "status") || "active",
  is_bundle: selector(state, "is_bundle"),
  is_virtual: selector(state, "is_virtual"),
}))(reduxForm({ form, enableReinitialize: true, validate })(ProductNew));
